import { inject, injectable } from 'inversify';

import { getMediaStatusFromEventState } from '../../../utils/liveEvent';
import { createURL } from '../../../utils/urlFormatting';
import { getDataOrThrow } from '../../../utils/api';
import { filterMediaOffers } from '../../../utils/entitlements';
import { useConfigStore as ConfigStore } from '../../../stores/ConfigStore';
import type { GetPlaylistParams, Playlist, PlaylistItem } from '../../../../types/playlist';
import env from '../../../env';
import ContentApiService from '../../ContentApiService';
import type { Config } from '../../../../types/config';

import VDApiService from './VDApiService';

@injectable()
export default class VDContentApiService extends ContentApiService {
  constructor(@inject(VDApiService) protected readonly apiService: VDApiService) {
    super();
  }

  transformMediaItem = ({ item, language }: { item: PlaylistItem; playlist?: Playlist; language?: string }) => {
    const config = ConfigStore.getState().config;
    const offerKeys = Object.keys(config?.integrations)[0];
    const translatedFields = this.getTranslatedFields(item, language);

    const transformedMediaItem = {
      ...item,
      ...translatedFields,
      cardImage: item.image,
      channelLogoImage: item.image,
      backgroundImage: item.images.reverse()[0]?.src || item.image,
      mediaOffers: item.productIds ? filterMediaOffers(offerKeys, item.productIds) : undefined,
      scheduledStart: this.parseDate(item, 'VCH.ScheduledStart'),
      scheduledEnd: this.parseDate(item, 'VCH.ScheduledEnd'),
    };

    // add the media status to the media item after the transformation because the live media status depends on the scheduledStart and scheduledEnd
    transformedMediaItem.mediaStatus = getMediaStatusFromEventState(transformedMediaItem);

    return transformedMediaItem;
  };

  /**
   * Get media by id
   */
  getMediaById = async ({ id, token, language, config }: { id: string; token?: string; language?: string; config: Config }): Promise<PlaylistItem> => {
    const drmPolicyId = (config.contentProtection?.drm?.defaultPolicyId || config.custom?.drmPolicyId) as string | undefined;
    const pathname = drmPolicyId ? `/media/${id}/drm/${drmPolicyId}` : `/media/${id}`;
    const url = createURL(`${this.BASE_URL}${pathname}`, { token });
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;
    const mediaItem = data.playlist[0];

    if (!mediaItem) throw new Error('MediaItem not found');

    return this.transformMediaItem({ item: mediaItem, language });
  };

  getPlaylistById = async ({
    id,
    params = {},
    language = env.APP_DEFAULT_LANGUAGE,
    config,
  }: {
    id?: string;
    params?: GetPlaylistParams;
    language?: string;
    config?: Config;
  }): Promise<Playlist> => {
    if (!id) {
      throw new Error('id is required');
    }

    // @todo: ideally we create a separate token for the content api
    // This way we can disconnect it from authentication purposes
    const { token } = await this.apiService.getToken();
    const options = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

    const playlistPath = `/playlist/${id}`;
    const recommendationsPath = `/related/${id}`;

    const pathname = params.related_media_id ? recommendationsPath : playlistPath;
    const url = createURL(`${this.BASE_URL}${pathname}`, { ...params, configId: config?.id });
    const response = await fetch(url, options);
    const data = (await getDataOrThrow(response)) as Playlist;

    return this.transformPlaylist(data, params.related_media_id, language);
  };

  search = async ({ searchTerm, config }: { searchTerm: string; config: Config }) => {
    const pathname = `/search`;

    const url = createURL(`${this.BASE_URL}${pathname}`, {
      search_query: searchTerm,
      configId: config?.id,
    });

    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;

    return this.transformPlaylist(data);
  };

  protected getMediaItems = async ({ ids, token, language, config }: { ids: string[]; token?: string; language?: string; config: Config }) => {
    const results = await Promise.allSettled(ids.map((id) => this.getMediaById({ id, token, language, config })));

    return results
      .map((promiseResult) => {
        return promiseResult.status === 'fulfilled' ? promiseResult.value : undefined;
      })
      .filter((item) => !!item);
  };

  getWatchHistoryMediaItems = async ({ ids, language, token, config }: { ids: string[]; language?: string; token?: string; config: Config }) => {
    return this.getMediaItems({ ids, config, language, token });
  };

  getFavoriteMediaItems = async ({ ids, language, token, config }: { ids: string[]; language?: string; token?: string; config: Config }) => {
    return this.getMediaItems({ ids, config, language, token });
  };

  getSeriesByMediaIds = async () => {
    // not supported by VD integration
    return {};
  };
}
